// packages/ui/src/tools/ToolPanelTabs.tsx
import { useState, type ReactElement } from 'react';
import { X, Plus } from 'lucide-react';
import { Modal } from '../components/Modal';
import { InlineEditInput } from '../components/InlineEditInput';
import { useContextMenu, type ContextMenuEntry } from '../context-menu';
import { useToolsStore, type ToolInstance } from './toolsStore';
import { ToolRegistry } from './ToolRegistry';

interface Props {
  onNewTool: (toolId: string) => void;
  /** Capability-filtered tool list. */
  availableToolIds: string[];
}

export function ToolPanelTabs({ onNewTool, availableToolIds }: Props): ReactElement {
  const instances = useToolsStore((s) => s.instances);
  const activeId = useToolsStore((s) => s.activeInstanceId);
  const setActive = useToolsStore((s) => s.setActiveInstance);
  const closeInstance = useToolsStore((s) => s.closeInstance);
  const renameInstance = useToolsStore((s) => s.renameInstance);
  const { openContextMenu } = useContextMenu();

  // A busy terminal tab awaiting close confirmation (inline, per-tab).
  const [confirmingClose, setConfirmingClose] = useState<ToolInstance | null>(null);
  // Inline rename. `null` when no tab is being renamed.
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);

  const primaryToolId = availableToolIds[0];

  function requestClose(instance: ToolInstance): void {
    if (instance.busy && instance.toolId === 'terminal') {
      setConfirmingClose(instance);
    } else {
      closeInstance(instance.id);
    }
  }

  function commitRename(): void {
    if (!renaming) return;
    const { id, value } = renaming;
    setRenaming(null);
    // renameInstance handles the empty case (clears the pin, reverts to the
    // auto title) so an empty rename means "follow the running command again".
    renameInstance(id, value);
  }

  function buildTabMenu(instance: ToolInstance): ContextMenuEntry[] {
    const others = instances.filter((i) => i.id !== instance.id);
    return [
      {
        id: 'rename',
        label: 'Rename',
        onSelect: () => setRenaming({ id: instance.id, value: instance.title }),
      },
      { id: 'close', label: 'Close', onSelect: () => requestClose(instance) },
      {
        id: 'close-others',
        label: 'Close others',
        disabled: others.length === 0,
        onSelect: () => others.forEach((i) => requestClose(i)),
      },
      {
        id: 'close-all',
        label: 'Close all',
        disabled: instances.length === 0,
        onSelect: () => instances.forEach((i) => requestClose(i)),
      },
    ];
  }

  return (
    <div className="mp-tabs mp-tool-tabs" role="tablist" aria-label="Tool tabs">
      {instances.map((i) => (
        <Tab
          key={i.id}
          instance={i}
          active={i.id === activeId}
          renaming={renaming?.id === i.id ? renaming.value : null}
          onActivate={() => setActive(i.id)}
          onClose={() => requestClose(i)}
          onStartRename={() => setRenaming({ id: i.id, value: i.title })}
          onRenameChange={(v) => setRenaming((s) => (s ? { ...s, value: v } : s))}
          onRenameCommit={commitRename}
          onRenameCancel={() => setRenaming(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openContextMenu(e.clientX, e.clientY, buildTabMenu(i));
          }}
        />
      ))}
      <button
        type="button"
        className="mp-tab-add"
        aria-label="New tool"
        title="New tool"
        disabled={!primaryToolId}
        onClick={() => primaryToolId && onNewTool(primaryToolId)}
      >
        <Plus size={14} aria-hidden />
      </button>

      {confirmingClose && (
        <Modal
          open
          onClose={() => setConfirmingClose(null)}
          title={`Close ${confirmingClose.title}?`}
          footer={
            <>
              <button
                type="button"
                className="mp-btn"
                onClick={() => setConfirmingClose(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mp-btn mp-btn--primary"
                onClick={() => {
                  closeInstance(confirmingClose.id);
                  setConfirmingClose(null);
                }}
              >
                Close anyway
              </button>
            </>
          }
        >
          <p className="mp-modal__text">
            A process is running in this terminal. Closing it will send SIGHUP to the
            process.
          </p>
        </Modal>
      )}
    </div>
  );
}

interface TabProps {
  instance: ToolInstance;
  active: boolean;
  renaming: string | null;
  onActivate: () => void;
  onClose: () => void;
  onStartRename: () => void;
  onRenameChange: (v: string) => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

function Tab({
  instance,
  active,
  renaming,
  onActivate,
  onClose,
  onStartRename,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
  onContextMenu,
}: TabProps): ReactElement {
  const def = ToolRegistry.getById(instance.toolId);
  const label = def?.title ?? instance.toolId;
  const isRenaming = renaming !== null;
  return (
    <div
      role="tab"
      aria-selected={active}
      data-tool-id={instance.toolId}
      data-active={active || undefined}
      className={`mp-tab mp-tab-label${instance.busy ? ' mp-tab--busy' : ''}`}
      onClick={onActivate}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onStartRename();
      }}
      onContextMenu={onContextMenu}
    >
      {isRenaming ? (
        <InlineEditInput
          value={renaming ?? ''}
          onChange={onRenameChange}
          onCommit={onRenameCommit}
          onCancel={onRenameCancel}
          ariaLabel={`Rename ${instance.title}`}
          className="mp-tool-tab__rename"
          selectOnMount="select"
        />
      ) : (
        <span className="mp-tab__label">{instance.title || label}</span>
      )}
      <button
        type="button"
        aria-label={`Close ${instance.title}`}
        title="Close"
        className="mp-tab__close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}
